import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';

import { DeviceClaimingApiService } from '@shure/cloud/device-management/devices/data-access';
import { AddDevicesDialogLaunchedEvent } from '@shure/cloud/device-management/shared/analytics';
import {
	PrismDialogRef,
	PrismStepper,
	PrismStepperComponent,
	PrismStepperDialogComponent
} from '@shure/prism-angular-components';
import {
	provideStepRouter,
	StepRouter,
	StepRouterContentOutletComponent,
	StepRouterFooterOutletComponent
} from '@shure/shared/angular/ui/components/step-router';
import { AnalyticsService } from '@shure/shared/angular/utils/analytics';

import { AddDevicesDialogService, MacSerialNumberDevice } from '../../../services/add-devices-dialog.service';
import { ClaimingFlowService } from '../../../services/claiming-flow.service';

import { Step1HowToFindConfig } from './step1-how-to-find/step1-how-to-find.config';
import { Step2DiscoverDevicesConfig } from './step2-scanning/step2-discover-devices.config';
import { Step3ConfirmationConfig } from './step3-confirmation/step3-confirmation.config';

@Component({
	selector: 'sh-add-devices-dialog',
	imports: [
		CommonModule,
		TranslocoModule,
		FormsModule,
		PrismStepperComponent,
		PrismStepperDialogComponent,
		StepRouterContentOutletComponent,
		StepRouterFooterOutletComponent
	],
	templateUrl: './add-devices-dialog.component.html',
	styleUrl: './add-devices-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		AddDevicesDialogService,
		...provideStepRouter({
			steps: [Step1HowToFindConfig, Step2DiscoverDevicesConfig, Step3ConfirmationConfig]
		})
	]
})
export class AddDevicesDialogComponent implements AfterViewInit, OnDestroy {
	private readonly stepper = viewChild<PrismStepper>('stepper');

	constructor(
		public readonly dialogRef: PrismDialogRef<AddDevicesDialogComponent>,
		public readonly addDevicesDialogService: AddDevicesDialogService,
		public readonly deviceClaimingService: DeviceClaimingApiService,
		private readonly claimingFlowService: ClaimingFlowService,
		private readonly stepRouter: StepRouter,
		private readonly analyticsService: AnalyticsService
	) {}

	public ngAfterViewInit(): void {
		this.sendAddDevicesDialogLaunchedEvent();

		const stepper = this.stepper();
		if (!stepper) {
			throw new Error('Unable to get instance of PrismStepper');
		}

		this.stepRouter.setStepper(stepper);

		const goToClaimLinkSelectionStep = this.claimingFlowService.readGoToClaimLinkSelectionStepFlag();
		if (goToClaimLinkSelectionStep) {
			// Show device selection step with org switcher when final=true is provided.
			this.addDevicesDialogService.setClaimByLink(this.getClaimLinkDevices());
			setTimeout(() => {
				this.stepRouter.nextStep();
			});
			return;
		}

		const goToQrClaimingStep = this.claimingFlowService.readGoToQrClaimingStepFlag();
		if (goToQrClaimingStep) {
			this.addDevicesDialogService.setClaimByQrCode();
			setTimeout(() => {
				this.stepRouter.nextStep();
			});
		}
	}

	public ngOnDestroy(): void {
		this.claimingFlowService.clear();
	}

	private getClaimLinkDevices(): MacSerialNumberDevice[] {
		return this.claimingFlowService.getDevices().map((code) => ({
			device: {
				id: code.mac,
				macAddress: code.mac,
				serialNumber: code.serial
			}
		}));
	}

	private sendAddDevicesDialogLaunchedEvent(): void {
		this.analyticsService.trackEvent(new AddDevicesDialogLaunchedEvent());
	}
}
